<script setup>
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'next/button/Button.vue';
import Dropdown from 'next/wgpt/Dropdown/Dropdown.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import CreateSamlProvider from './components/CreateSamlProviderDropdown.vue';
import DeleteSamlProviderDialog from './components/DeleteSamlProviderDialog.vue';

const { t } = useI18n();

const store = useStore();
const samlProviders = useMapGetter('wgptSamlProviders/get');
const uiFlags = useMapGetter('wgptSamlProviders/getUIFlags');

const deleteSamlProviderDialogRef = ref(null);

const openDelete = provider => {
  deleteSamlProviderDialogRef.value?.open(provider);
};

onMounted(async () => {
  store.dispatch('wgptSamlProviders/get');
});
</script>

<template>
  <SettingsLayout
    :no-records-found="!samlProviders.length"
    :no-records-message="$t('WGPT_SAML.PROVIDERS.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('WGPT_SAML.PROVIDERS.LIST.HEADER')"
        :description="$t('WGPT_SAML.PROVIDERS.LIST.DESCRIPTION')"
      >
        <template #actions>
          <Dropdown align="end">
            <template #trigger="{ toggle }">
              <Button
                :label="t('WGPT_SAML.PROVIDERS.LIST.NEW_PROVIDER')"
                icon="i-lucide-plus"
                size="sm"
                @click="toggle"
              />
            </template>
            <template #default="{ toggle }">
              <CreateSamlProvider @close="toggle" />
            </template>
          </Dropdown>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full divide-y divide-slate-75 dark:divide-slate-700">
        <thead>
          <th
            class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-slate-700 dark:text-slate-300"
          >
            {{ $t('WGPT_SAML.PROVIDERS.LIST.TABLE.DISPLAY_NAME') }}
          </th>
        </thead>

        <tbody
          class="divide-y divide-slate-50 dark:divide-slate-800 text-slate-700 dark:text-slate-300"
        >
          <tr v-for="samlProvider in samlProviders" :key="samlProvider.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              {{ samlProvider.display_name }}
            </td>

            <td class="py-4 flex justify-end gap-1">
              <router-link
                :to="{
                  name: 'wgpt_saml_providers_edit',
                  params: { samlProviderId: samlProvider.id },
                }"
              >
                <Button icon="i-lucide-pencil" slate xs faded />
              </router-link>
              <Button
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                @click="openDelete(samlProvider)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <DeleteSamlProviderDialog ref="deleteSamlProviderDialogRef" />
  </SettingsLayout>
</template>
